import { FormEvent, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import toast from "react-hot-toast";
import { submitFeedback } from "../../utils/submitFeedback";

export const MAX_FEEDBACK_LENGTH = 5000;

type FeedbackModalProps = {
  show: boolean;
  onHide: () => void;
};

/** Free-text product feedback. Always opens empty; past submissions are never shown back to the user. */
const FeedbackModal = ({ show, onHide }: FeedbackModalProps) => {
  const [text, setText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      setText("");
    }
  }, [show]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!text.trim() || isSubmitting) {
      return;
    }
    setIsSubmitting(true);
    try {
      await submitFeedback(text);
      toast.success("Thank you! Your feedback has been received.");
      setText("");
      onHide();
    } catch (err) {
      toast.error("Sorry, we couldn't send your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton closeLabel="Close">
        <Modal.Title>Share your feedback</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form id="feedback-form" onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="feedback-text">
            <p>
              We take feedback very seriously and would love to hear how we can improve. Whether there's something
              you like or something you don't, please let us know.
            </p>
            <Form.Label>Your feedback</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              required
              name="text"
              placeholder="I like… / I don't like…"
              maxLength={MAX_FEEDBACK_LENGTH}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isSubmitting}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSubmitting}>
          Close
        </Button>
        <Button variant="primary" form="feedback-form" type="submit" disabled={isSubmitting || !text.trim()}>
          {isSubmitting ? "Sending…" : "Send feedback"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FeedbackModal;
